import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { RouterLink } from '@angular/router';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { TranslatePipe } from '@ngx-translate/core';

interface ProductLink {
  name: string;
  logoSrc: string;
  url: string;
  external: boolean;
}

/** Same four products shown on the marketing /products page (ProductsPage.SHOWCASES),
 *  reduced to just a logo + link for this card's compact tile row. */
const PRODUCTS: readonly ProductLink[] = [
  { name: 'Chroniq', logoSrc: 'assets/icon/chroniq-title.png', url: '/contact', external: false },
  { name: 'TableStack', logoSrc: 'assets/icon/tablestack.png', url: '/contact', external: false },
  { name: 'ScreenDeck', logoSrc: 'assets/icon/screendeck-wordmark.svg', url: 'https://www.screendeck.ch', external: true },
  { name: 'MenuLink', logoSrc: 'assets/icon/menulink.png', url: '/contact', external: false },
];

/** vCard 3.0: broadest device support (older Android/iOS included). Contact data mirrors
 *  the Impressum (imprint.page.html) — the one place this identity is already maintained. */
const VCARD = [
  'BEGIN:VCARD',
  'VERSION:3.0',
  'N:Oberbillig;Erik Alexander;;;',
  'FN:Erik Alexander Oberbillig',
  'ORG:Gastro Management Oberbillig',
  'TITLE:Inhaber',
  'EMAIL;TYPE=WORK:management@gastro-management.ch',
  'URL:https://gastro-management.ch',
  'ADR;TYPE=WORK:;;Kirchenrütti 14;Bürglen UR;;6463;Schweiz',
  'END:VCARD',
].join('\r\n');

@Component({
    selector: 'app-card',
    templateUrl: './card.page.html',
    styleUrls: ['./card.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PortalNavbarComponent,
        IonicModule,
        RouterLink,
        PortalFooterComponent,
        TranslatePipe,
    ],
})
export class CardPage implements OnInit, OnDestroy {
  readonly products = PRODUCTS;

  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private seo: SeoService,
    private readonly portalAuth: PortalAuthService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'card.seoTitle', descriptionKey: 'card.seoDescription', path: '/card' });

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadVCard(): void {
    const blob = new Blob([VCARD], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'erik-oberbillig.vcf';
    link.click();
    URL.revokeObjectURL(url);
  }
}
